import type { ColumnDef } from '@tanstack/react-table';
import { CopyButton } from '@fiado/react-kit';
import { SortableHeader } from '../shared/SortableHeader';
import { principalIdentifierLabel, resolveIdentifierDisplay } from '../../lib/identifierDisplay';
import type { PrincipalIdentifierLocal } from '../../api/types/PlatformLocal';
import type { TenantUserListItemLocal } from '../../api/types/TenantLocal';

/**
 * DEC-RBAC-079/081 — Columna del IDENTIFICADOR de una tabla de usuarios, compartida por
 * `PlatformTenantUsersTable` (contexto platform) y `ScopedUsersTable` (contexto tenant-admin).
 *
 * Se construye en tiempo de render (no a nivel de módulo) porque depende del `principalIdentifier` del
 * tenant (dato async del hook `useTenantIdentifier` / `useMyTenantIdentifier`). Muestra el identificador
 * PRINCIPAL del tenant (Teléfono si phone-primary, Email si email-primary). Un sistema (API) no tiene
 * correo real: muestra su clientId abreviado y copiable.
 */
export function buildIdentifierColumn(
    principalIdentifier: PrincipalIdentifierLocal,
): ColumnDef<TenantUserListItemLocal> {
    const label = principalIdentifierLabel(principalIdentifier);
    return {
        id: 'identifier',
        // Ordena/filtra por el dato principal; el de un sistema es su clientId (= cognitoSub).
        accessorFn: (row) =>
            row.accountType === 'API'
                ? row.cognitoSub
                : resolveIdentifierDisplay(row, principalIdentifier).primary,
        header: ({ column }) => <SortableHeader column={column} title={label} />,
        cell: (info) => {
            const row = info.row.original;
            if (row.accountType === 'API') {
                return (
                    <span className="inline-flex items-center gap-1.5">
                        <span className="whitespace-nowrap text-xs font-medium text-muted-foreground">
                            Client ID
                        </span>
                        <CopyButton value={row.cognitoSub} label="Client ID" truncateAt={8} />
                    </span>
                );
            }
            const { primary } = resolveIdentifierDisplay(row, principalIdentifier);
            return <span>{primary}</span>;
        },
    };
}
